import React from "react";
import {
  Dialog,
  Box,
  Stack,
  Typography,
  Button,
  IconButton,
  CircularProgress,
} from "@mui/material";
import {
  Close as CloseIcon,
  RestoreOutlined as RestoreOutlinedIcon,
} from "@mui/icons-material";

import useOrganisation from "../../../../hooks/super/useOrganisation";

const TOKENS = {
  primary: "#6371F7",
  primaryHover: "#0244BE",
  danger: "#FB3748",
  dangerHover: "#D00416",
  dangerBg: "#FEECEE",
  success: "#1FC16B",
  successHover: "#1DD75B",
  successBg: "#E8FBEF",
  successInk: "#0f7a42",
  fg1: "#292929",
  fg2: "#7B7B7B",
  fg3: "#959595",
  border: "#D4D4D4",
  borderSubtle: "rgba(0,0,0,0.08)",
  bgSurface: "#FFFFFF",
  bgSunken: "#F7F7F9",
  bgOverlay: "rgba(17,17,17,0.5)",
  shadowL: "0 12px 32px rgba(17,17,17,0.08), 0 4px 8px rgba(17,17,17,0.04)",
  shadowFocus: "0 0 0 3px rgba(99,113,247,0.22)",
};

const MONO_COLORS = [
  ["#EBEDF5", "#454FAD"],
  ["#EDF3FE", "#0255ED"],
  ["#E8FBEF", "#0f7a42"],
  ["#FFF6D0", "#916C19"],
  ["#FEECEE", "#A11228"],
  ["#F3E7FF", "#7A3FB8"],
  ["#E6F7F6", "#0E7C75"],
  ["#FDEFE6", "#B5500F"],
];

function monoFor(name = "") {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return MONO_COLORS[h % MONO_COLORS.length];
}

function initials(name = "") {
  const parts = String(name).replace(/[^A-Za-z0-9 ]/g, "").trim().split(/\s+/);
  if (!parts[0]) return "??";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return ((parts[0][0] || "") + (parts[1][0] || "")).toUpperCase();
}

const OrgRow = ({ org }) => {
  const [bg, fg] = monoFor(org.title || "");
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1.25}
      sx={{
        px: "12px",
        py: "10px",
        "&:not(:last-of-type)": {
          borderBottom: `1px solid ${TOKENS.borderSubtle}`,
        },
      }}
    >
      <Box
        sx={{
          width: 28,
          height: 28,
          flex: "0 0 auto",
          borderRadius: "8px",
          background: bg,
          color: fg,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          fontWeight: 700,
          fontSize: 11,
          letterSpacing: 0.4,
        }}
      >
        {initials(org.title || "")}
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          sx={{
            fontSize: 13,
            fontWeight: 500,
            color: TOKENS.fg1,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {org.title || "(untitled)"}
        </Typography>
        <Typography
          sx={{
            fontFamily: '"SF Mono", Menlo, Consolas, "Liberation Mono", monospace',
            fontSize: 11,
            color: TOKENS.fg3,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {org.id}
        </Typography>
      </Box>
    </Stack>
  );
};

function RestoreOrganisationModal({ open, handleClose, orgID, orgName }) {
  const [restoring, setRestoring] = React.useState(false);
  const { restoreOrganisation } = useOrganisation();

  const orgs = Array.isArray(orgName) ? orgName : [];
  const count = orgs.length;

  const title =
    count > 1
      ? `Restore ${count} organisations?`
      : "Restore organisation?";

  const body =
    "This re-activates the organisation and all of its sites and users. Any associated subscriptions are NOT automatically restored.";

  const onClose = () => {
    if (restoring) return;
    handleClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: 480,
          maxWidth: "92vw",
          borderRadius: 2,
          overflow: "hidden",
          boxShadow: TOKENS.shadowL,
          m: 2,
        },
      }}
      BackdropProps={{ sx: { backgroundColor: TOKENS.bgOverlay } }}
    >
      <Box sx={{ p: "24px" }}>
        {/* Header row: icon + title + close */}
        <Stack direction="row" spacing={1.75} alignItems="flex-start">
          <Box
            sx={{
              width: 42,
              height: 42,
              flex: "0 0 auto",
              borderRadius: "11px",
              backgroundColor: TOKENS.successBg,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <RestoreOutlinedIcon sx={{ fontSize: 20, color: TOKENS.successInk }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0, pt: 0.25 }}>
            <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
              <Typography
                sx={{
                  fontSize: 17,
                  fontWeight: 600,
                  color: TOKENS.fg1,
                  letterSpacing: -0.1,
                }}
              >
                {title}
              </Typography>
              <IconButton
                onClick={onClose}
                size="small"
                disabled={restoring}
                sx={{
                  mt: "-6px",
                  mr: "-6px",
                  color: TOKENS.fg2,
                  "&:hover": { color: TOKENS.fg1 },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Stack>
            <Typography
              sx={{
                mt: 0.75,
                fontSize: 13.5,
                color: TOKENS.fg2,
                lineHeight: 1.5,
              }}
            >
              {body}
            </Typography>
          </Box>
        </Stack>

        {/* Org list */}
        {orgs.length > 0 && (
          <Box
            sx={{
              mt: 2,
              border: `1px solid ${TOKENS.borderSubtle}`,
              borderRadius: "10px",
              maxHeight: 168,
              overflow: "auto",
              backgroundColor: TOKENS.bgSurface,
            }}
          >
            {orgs.map((org) => (
              <OrgRow key={org.id} org={org} />
            ))}
          </Box>
        )}

        {/* Actions */}
        <Stack
          direction="row"
          justifyContent="flex-end"
          spacing={1}
          sx={{ mt: 3 }}
        >
          <Button
            type="button"
            onClick={onClose}
            disableElevation
            disabled={restoring}
            sx={{
              height: 40,
              px: 2,
              borderRadius: "8px",
              border: `1px solid ${TOKENS.border}`,
              backgroundColor: "transparent",
              color: TOKENS.fg1,
              fontSize: 13,
              fontWeight: 500,
              textTransform: "none",
              "&:hover": { backgroundColor: TOKENS.bgSunken, borderColor: TOKENS.fg3 },
            }}
            startIcon={<CloseIcon sx={{ fontSize: 16 }} />}
          >
            Cancel
          </Button>
          <Button
            type="button"
            disableElevation
            disabled={restoring}
            onClick={async () => {
              setRestoring(true);
              try {
                await restoreOrganisation(orgID);
              } finally {
                setRestoring(false);
                handleClose();
              }
            }}
            sx={{
              height: 40,
              px: 2,
              borderRadius: "8px",
              backgroundColor: TOKENS.success,
              color: "#fff",
              fontSize: 13,
              fontWeight: 600,
              textTransform: "none",
              "&:hover": { backgroundColor: TOKENS.successHover },
              "&.Mui-disabled": {
                backgroundColor: TOKENS.success,
                color: "#fff",
                opacity: 0.6,
                cursor: "default",
              },
            }}
            startIcon={
              restoring ? (
                <CircularProgress size={14} sx={{ color: "#fff" }} />
              ) : (
                <RestoreOutlinedIcon sx={{ fontSize: 16 }} />
              )
            }
          >
            {restoring ? "Restoring…" : count > 1 ? `Restore ${count}` : "Restore"}
          </Button>
        </Stack>
      </Box>
    </Dialog>
  );
}

export default RestoreOrganisationModal;
